"use client"
import React, { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../context/AuthContext"
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner"
import { useToast } from "../../context/ToastContext";
import DirectionArrow from "../Connector-Screen/ChooseSync/DirectionArrow"
import "./Dashboard.css"

// Type definitions
interface Connector {
  tenant_connector_id: string
  tenant_id: string
  connector_name: string
  source_system: string
  source_object: string
  target_system: string
  target_object: string
  updated_at : string
  sync_direction: "bidirectional" | "one-way"
  status: 'active' | 'inactive' | 'error' | 'syncing'
  organization?: {
    organization_name: string
  }
}

interface ToggleSwitchProps {
  isActive: boolean
  onToggle: () => void
  disabled?: boolean
}

interface StatusBadgeProps {
  status: Connector['status']
}

interface ConnectorCardProps {
  connector: Connector
  onToggleStatus: (connectorId: string, newStatus: Connector['status']) => Promise<void>
  onDeleteConnector: (connectorId: string) => Promise<void>
}

// Loading component
const LoadingScreen: React.FC = () => (
  <div className="loading-screen">
    <div className="loading-content">
      <LoadingSpinner size="large" />
      <h2>Loading Field Mappings</h2>
      <p>Please wait while we fetch your field data...</p>
    </div>
  </div>
)

const InfoIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    shapeRendering="geometricPrecision"
    textRendering="geometricPrecision"
    imageRendering="optimizeQuality"
    fillRule="evenodd"
    clipRule="evenodd"
    viewBox="0 0 512 512"
  >
    <path
      fill="#CF8305"
      fillRule="nonzero"
      d="M256 0c70.689 0 134.692 28.656 181.016 74.98C483.344 121.308 512 185.311 512 256c0 70.689-28.656 134.692-74.984 181.016C390.692 483.344 326.689 512 256 512s-134.692-28.656-181.02-74.984C28.656 390.692 0 326.689 0 256S28.656 121.308 74.98 74.98C121.308 28.656 185.311 0 256 0z"
    />
    <circle fill="#FFA107" cx="256" cy="256" r="241.831" />
    <path
      fill="#CF8305"
      fillRule="nonzero"
      d="M252.33 144.551c5.039-12.2 17.404-20.536 30.609-20.536 18.611 0 32.717 15.259 32.717 33.478 0 4.53-.796 8.776-2.407 12.704-6.902 16.91-26.089 25.405-43.082 18.301-16.87-7.121-24.821-27.095-17.837-43.947zm12.103 206.605c-.833 2.983-2.256 7.946-.674 10.725 4.22 7.45 16.459-6.058 19.036-8.97 8.307-9.414 15.461-20.475 21.905-31.229a1.507 1.507 0 012.062-.523l13.439 9.972c.642.473.789 1.363.368 2.029-6.181 10.744-12.427 20.125-18.745 28.13-10.451 13.234-23.595 25.852-39.582 32.065-9.919 3.842-22.817 5.363-34.145 2.829-10.505-2.353-19.66-8.206-23.822-18.946-5.464-14.092-.969-30.105 3.33-43.887l21.689-65.697c2.962-10.647 10.044-29.662-8.25-29.662h-18.96c-1.561 0-1.597-1.401-1.298-2.483l4.858-17.685a1.5 1.5 0 011.464-1.103l96.89-3.038c1.409-.05 1.7 1.19 1.373 2.285l-40.938 135.188z"
    />
    <path
      fill="#fff"
      fillRule="nonzero"
      d="M242.884 135.105c5.039-12.2 17.404-20.536 30.609-20.536 18.611 0 32.717 15.259 32.717 33.478 0 4.53-.796 8.776-2.407 12.704-6.902 16.91-26.09 25.405-43.082 18.301-16.871-7.121-24.821-27.095-17.837-43.947zm12.103 206.604c-.833 2.984-2.256 7.947-.674 10.726 4.22 7.449 16.459-6.059 19.036-8.97 8.307-9.414 15.461-20.475 21.905-31.229a1.505 1.505 0 012.061-.523l13.44 9.972c.641.473.789 1.363.367 2.029-6.18 10.744-12.426 20.125-18.744 28.13-10.452 13.234-23.595 25.851-39.583 32.065-9.918 3.842-22.817 5.363-34.144 2.829-10.505-2.353-19.66-8.206-23.822-18.946-5.464-14.092-.97-30.105 3.33-43.887l21.689-65.697c2.962-10.647 10.044-29.662-8.25-29.662h-18.961c-1.56 0-1.596-1.402-1.297-2.483l4.858-17.685a1.5 1.5 0 011.464-1.103l96.89-3.038c1.409-.05 1.7 1.19 1.373 2.285l-40.938 135.187z"
    />
  </svg>
);

  const getObjectName = (objectName: string, isLower:boolean) =>{
    return isLower ? objectName.replace(objectName.charAt(0),objectName.charAt(0).toUpperCase()) : objectName.replace(objectName.charAt(0),objectName.charAt(0).toLowerCase())
  }


// Toggle Switch Component
const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ isActive, onToggle, disabled = false }) => (
  <button
    className={`toggle-switch ${isActive ? 'active' : ''} ${disabled ? 'disabled' : ''}`}
    onClick={onToggle}
    disabled={disabled}
    aria-label={isActive ? 'Deactivate connector' : 'Activate connector'}
  >
    <div className="toggle-slider"></div>
  </button>
)

// Status Badge Component
const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const getStatusConfig = (status: Connector['status']) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return { className: 'status-active', text: 'Active' }
      case 'inactive':
        return { className: 'status-inactive', text: 'Inactive' }
      case 'error':
        return { className: 'status-error', text: 'Error' }
      case 'syncing':
        return { className: 'status-syncing', text: 'Syncing' }
      default:
        return { className: 'status-unknown', text: status || 'Unknown' }
    }
  }

  const config = getStatusConfig(status)
  
  return (
    <span className={`status-badge ${config.className}`}>
      {config.text}
    </span>
  )
}

// Connector Card Component
const ConnectorCard: React.FC<ConnectorCardProps> = ({ connector, onToggleStatus, onDeleteConnector }) => {
  const [isToggling, setIsToggling] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const navigate = useNavigate()

  const handleToggle = async () => {
    setIsToggling(true)
    try {
      await onToggleStatus(connector.tenant_connector_id, connector.status === 'active' ? 'inactive' : 'active')
    } catch (error) {
      console.error('Error toggling connector status:', error)
    } finally {
      setIsToggling(false)
    }
  }


  const handleDelete = async () => {
    setIsToggling(true)
    try {
      await onDeleteConnector(connector.tenant_connector_id)
      setShowDeleteConfirm(false)
    } catch (error) {
      console.error('Error toggling connector status:', error)
    } finally {
      setIsToggling(false)
    }
  }

  
  const handleEditConnector = () => {
      navigate(`/dashboard/edit-connector/${connector.tenant_connector_id}/${connector.tenant_id}`)
    }

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    } catch (error) {
      return "Invalid date"
    }
  }

  /* Delete confirmation modal remains the same */
  if(showDeleteConfirm){
        return ( <>
        <div className="delete-modal-overlay">
          <div className="delete-modal">
            <div className="modal-header">
              <div className="modal-icon danger">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18l-2 13H5L3 6z" />
                  <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </div>
              <h3>Delete Connector</h3>
            </div>
            <p>
              Are you sure you want to delete this connector? This action cannot be undone and will stop all data
              synchronization.
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)} disabled={isToggling}>
                Cancel
              </button>
              <button className="btn-delete" onClick={handleDelete} disabled={isToggling}>
                {isToggling ? <LoadingSpinner size="small" /> : "Delete Connector"}
              </button>
            </div>
          </div>
        </div>
        </>
      )
  }


  

  return (
    <div className="dashoard-home connector-card">
      <div className="tanent">
          <strong>
            Tenant 
            <div className="tooltip">
              <div className="info-icon">
                <InfoIcon />
              </div>
              <span className="tooltiptext">
                <div className="tooltip-content">
                  <div className="tooltip-header">
                    <strong>Connector Details</strong>
                  </div>
                  <div className="tooltip-body">
                    <div className="tooltip-row">
                      <span className="tooltip-label">Tenant ID:</span>
                      <span className="tooltip-value">
                        {connector.tenant_id}
                      </span>
                    </div>
                    <div className="tooltip-row">
                      <span className="tooltip-label">Last Updated:</span>
                      <span className="tooltip-value">
                        {formatDate(connector.updated_at)}
                      </span>
                    </div>
                    <div className="tooltip-row">
                      <span className="tooltip-label">Connector:</span>
                      <span className="tooltip-value">
                        {connector.connector_name}
                      </span>
                    </div>
                  </div>
                </div>
              </span>
            </div>
          </strong><span>{connector.tenant_id}</span> <StatusBadge status={connector.status} />
      </div>
     
      <div className="connector-details">
        <div className="connector-flow">
          <div className="flow-item">
            <div className="flow-icon source">
              <div className="platform-logo hubspot">
                <img src="https://cdn.worldvectorlogo.com/logos/hubspot-1.svg" alt={connector.source_object} />
              </div>
            </div>
              <span>{getObjectName(connector.source_object, true)}</span>
          </div>
          <DirectionArrow direction={connector.sync_direction} />
          
          <div className="flow-item">
            <div className="flow-icon target">
              <div className="platform-logo salesforce">
                <img src="https://cdn.worldvectorlogo.com/logos/salesforce-2.svg" alt={connector.target_object} />
              </div>
            </div>
              <span>{getObjectName(connector.target_object, true)}</span>
          </div>
        </div>
        
      </div>
      
      <div className="connector-actions">
        <ToggleSwitch
          isActive={connector.status === 'active'}
          onToggle={handleToggle}
          disabled={isToggling}
        />
        <div className="action-button secondary" onClick={handleEditConnector}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
          {/* Edit */}
        </div>
        <div className="action-button danger" onClick={() => setShowDeleteConfirm(true)}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="3,6 5,6 21,6" />
            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
          {/* Delete */}
        </div> 
      </div>
    </div>
  )
}

const Dashboard: React.FC = () => {
  const { user, getSavedConnectors, updateConnectorConfig, deleteConnectorConfig } = useAuth()
  const [loading, setLoading] = useState(true)
  const [connectors, setConnectors] = useState<Connector[]>([])
  const { error, success } = useToast();
  const navigate = useNavigate()

  const handleCreateConnector = useCallback(() => {
    navigate("/dashboard/connectors")
  }, [navigate])


  const toggleConnectorStatus = async (connectorId: string, newStatus: Connector['status']) => {
    try {
      // This would be your actual API call to update connector status
      // For now, we'll simulate the update
      const response = await updateConnectorConfig({ status: newStatus , tenant_connector_id:connectorId  })

      if (response.status === 200) {
        let message = newStatus === 'active' ? 'Connector activated successfully' : 'Connector inactivated successfully'
        success(message)
        setConnectors(prev => prev.map(connector => 
          connector.tenant_connector_id === connectorId 
            ? { ...connector, status: newStatus }
            : connector
        ))
      } else {
        error("Failed to update connector status")
        throw new Error('Failed to update connector status')
      }
    } catch (error) {
      console.error('Error updating connector status:', error)
      // You might want to show a toast notification here
    }
  }
  const handleDeleteConnector = async (connectorId: string) => {
  try {
    const response = await deleteConnectorConfig({ connectorId })

    if (response.status === 200) {
      success("Connector deleted successfully")
      setConnectors(prev =>
        prev.filter(connector => connector.tenant_connector_id !== connectorId)
      )
    } else {
      error("Failed to delete connector")
      throw new Error("Failed to delete connector")
    }
  } catch (err) {
    console.error("Error deleting connector:", err)
    error("An error occurred while deleting connector")
  }
}
  

  useEffect(() => {
    const fetchConnectors = async () => {
      try {
        const response = await getSavedConnectors()
        if (response?.status === 200 && Array.isArray(response.data?.connectors)) {
          setConnectors(response.data.connectors)
        }
      } catch (error) {
        console.error("Error fetching connectors:", error)
      } finally {
        setLoading(false)
      }
    }

    fetchConnectors()
  }, [getSavedConnectors])

  if (loading) {
    return <LoadingScreen />
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-content">
        <main className="main-content">
          <div className="welcome-section">
            <h1 className="welcome-title">Welcome back, {user?.first_name || "User"}!</h1>
            <p className="welcome-subtitle">Manage your data synchronization connectors</p>
          </div>

          {connectors.length > 0 ? (
            <div className="connectors-section">
              <div className="connectors-header">
                <h2 className="connectors-title">Your Applications</h2>
                <button className="add-connector-button" onClick={handleCreateConnector}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="5" x2="12" y2="19" />
                    <line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Applications
                </button>
              </div>
              
              <div className="connectors-grid">
                {connectors.map((connector) => (
                  <ConnectorCard
                    key={connector.tenant_connector_id}
                    connector={connector}
                    onToggleStatus={toggleConnectorStatus}
                    onDeleteConnector={handleDeleteConnector}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="no-connector">
              <div className="no-connector-icon" onClick={handleCreateConnector}>
                <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 11h-4v4h-2v-4H7v-2h4V7h2v4h4v2z" />
                </svg>
              </div>
              <h3 className="no-connector-title">No application yet</h3>
              <p className="no-connector-text">Create your first application to start syncing data between platforms.</p>
              <button className="primary-button" onClick={handleCreateConnector}>
                Create Applications
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default Dashboard 